import React from 'react';
import { Card, CardContent, Typography, Box, LinearProgress, Chip, Tooltip, IconButton } from '@mui/material';
import { Info as InfoIcon, TrendingUp as TrendingUpIcon, TrendingDown as TrendingDownIcon } from '@mui/icons-material';
import Skeleton from '@mui/material/Skeleton';

const KpiCard = ({ title, value, unit, target, progress, trend, trendValue, color, icon: Icon, loading, chipValue, hasMeta = true, action }) => {
  const getStatusColor = () => {
    if (progress >= 90) return 'success';
    if (progress >= 70) return 'warning';
    return 'error';
  };

  const getTrendIcon = () => {
    if (trend === 'up') return <TrendingUpIcon fontSize="small" sx={{ color: 'success.main' }} />;
    if (trend === 'down') return <TrendingDownIcon fontSize="small" sx={{ color: 'error.main' }} />;
    return null;
  };

  const getTrendColor = () => {
    if (trend === 'up') return 'success.main';
    if (trend === 'down') return 'error.main';
    return 'text.secondary';
  };

  if (!hasMeta) {
    // Card simples, sem meta, barra, chip, trend
    return (
      <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <CardContent sx={{ flexGrow: 1, pb: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {Icon && (
                <Box
                  sx={{
                    backgroundColor: `${color}.light`,
                    borderRadius: '50%',
                    width: 40,
                    height: 40,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mr: 1.5,
                  }}
                >
                  <Icon sx={{ color: `${color}.main` }} />
                </Box>
              )}
              <Typography variant="h6" component="div">
                {loading ? <Skeleton width={120} /> : title}
              </Typography>
            </Box>
            {action ? action : (
              <Tooltip title="Mais informações">
                <IconButton size="small">
                  <InfoIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'baseline', mb: 1 }}>
            <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>
              {loading ? <Skeleton width={80} /> : value}
            </Typography>
            {unit && !loading && (
              <Typography variant="body2" color="text.secondary" sx={{ ml: 0.5 }}>
                {unit}
              </Typography>
            )}
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ flexGrow: 1, pb: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {Icon && (
              <Box
                sx={{
                  backgroundColor: `${color}.light`,
                  borderRadius: '50%',
                  width: 40,
                  height: 40,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  mr: 1.5,
                }}
              >
                <Icon sx={{ color: `${color}.main` }} />
              </Box>
            )}
            <Typography variant="h6" component="div">
              {loading ? <Skeleton width={120} /> : title}
            </Typography>
          </Box>
          {action ? action : (
            <Tooltip title="Mais informações">
              <IconButton size="small">
                <InfoIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'baseline', mb: 1 }}>
          <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>
            {loading ? <Skeleton width={80} /> : value}
          </Typography>
          {unit && !loading && (
            <Typography variant="body2" color="text.secondary" sx={{ ml: 0.5 }}>
              {unit}
            </Typography>
          )}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {loading ? (
              <>
                <Skeleton variant="circular" width={20} height={20} sx={{ mr: 0.5 }} />
                <Skeleton width={32} />
              </>
            ) : (
              <>
                {getTrendIcon()}
                <Typography
                  variant="body2"
                  sx={{ ml: 0.5, color: getTrendColor() }}
                >
                  {trendValue}
                </Typography>
              </>
            )}
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
            {loading ? <Skeleton width={60} /> : 'vs meta'}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography variant="caption" color="text.secondary">
            {loading ? <Skeleton width={60} /> : `Meta: ${target}`}
          </Typography>
          {loading ? (
            <Skeleton variant="rounded" width={40} height={20} />
          ) : (
            <Chip
              label={typeof chipValue !== 'undefined' ? chipValue : `${progress}%`}
              size="small"
              color={getStatusColor()}
              sx={{ height: 20, fontSize: '0.7rem' }}
            />
          )}
        </Box>
        {loading ? (
          <Skeleton variant="rounded" height={6} sx={{ borderRadius: 3 }} />
        ) : (
          <LinearProgress
            variant="determinate"
            value={progress}
            color={getStatusColor()}
            sx={{ height: 6, borderRadius: 3 }}
          />
        )}
      </CardContent>
    </Card>
  );
};

export default KpiCard;
